/* eslint-disable */
import React, {useState, useEffect, useMemo} from 'react'
import {Alert, Badge, Button, Modal, Spinner, Table} from 'react-bootstrap'
import {Link} from 'react-router-dom'
import {useDispatch, useSelector} from 'react-redux'
import {
  edmGetSession,
  selectEdmSessionDetail,
  selectEdmSessionDetailLoading,
  selectEdmError,
  clearError,
} from '../../../redux/slices/edminvoice.slice'

function formatAmount(amount, currency) {
  if (amount == null || isNaN(Number(amount))) return '-'
  const formatted = Number(amount).toLocaleString('tr-TR', {minimumFractionDigits: 2})
  return currency ? `${formatted} ${currency}` : formatted
}

function ImportResultModal({show, result, onClose, modalTitle = 'Aktarım Tamamlandı'}) {
  const dispatch = useDispatch()
  const sessionDetail = useSelector(selectEdmSessionDetail)
  const loading = useSelector(selectEdmSessionDetailLoading)
  const detailError = useSelector(selectEdmError)

  const [statusFilter, setStatusFilter] = useState('all')

  useEffect(() => {
    if (show && result?.importSessionId) {
      setStatusFilter('all')
      dispatch(edmGetSession(result.importSessionId))
    }
  }, [show, result?.importSessionId, dispatch])

  const hasFailures = (result?.failureCount ?? 0) > 0
  const hasSuccesses = (result?.successCount ?? 0) > 0

  const logs = useMemo(() => sessionDetail?.logs || [], [sessionDetail])

  const hasErrorColumn = useMemo(() => logs.some((log) => !log.isSuccess), [logs])

  const filteredLogs = useMemo(() => {
    if (statusFilter === 'success') return logs.filter((l) => l.isSuccess)
    if (statusFilter === 'failure') return logs.filter((l) => !l.isSuccess)
    return logs
  }, [logs, statusFilter])

  const totalsByCurrency = useMemo(() =>
    filteredLogs.reduce((acc, log) => {
      const cur = log.currency || 'TRY'
      acc[cur] = (acc[cur] || 0) + (Number(log.payableAmount) || 0)
      return acc
    }, {}),
    [filteredLogs]
  )

  return (
    <Modal show={show} onHide={onClose} size='lg' centered>
      <Modal.Header closeButton>
        <Modal.Title>{modalTitle}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Summary cards */}
        <div className='d-flex gap-4 mb-6'>
          <div className='flex-grow-1 p-4 rounded bg-light-success text-center'>
            <div className='fs-1 fw-bold text-success'>{result?.successCount ?? 0}</div>
            <div className='text-success fw-semibold'>Başarılı</div>
          </div>
          {hasFailures && (
            <div className='flex-grow-1 p-4 rounded bg-light-danger text-center'>
              <div className='fs-1 fw-bold text-danger'>{result?.failureCount ?? 0}</div>
              <div className='text-danger fw-semibold'>Hatalı</div>
            </div>
          )}
        </div>

        {/* Detail error */}
        {detailError && !loading && (
          <Alert variant='danger' onClose={() => dispatch(clearError())} dismissible>
            Aktarım detayı yüklenemedi: {detailError}
          </Alert>
        )}

        {loading && (
          <div className='text-center py-6'>
            <Spinner animation='border' />
          </div>
        )}

        {!loading && !detailError && sessionDetail && (
          <>
            {/* Status filter tabs — only shown when both outcomes exist */}
            {hasSuccesses && hasFailures && (
              <div className='d-flex gap-2 mb-3'>
                {[
                  {key: 'all', label: `Tümü (${logs.length})`},
                  {key: 'success', label: `Başarılı (${result?.successCount ?? 0})`, variant: 'success'},
                  {key: 'failure', label: `Hatalı (${result?.failureCount ?? 0})`, variant: 'danger'},
                ].map(({key, label, variant}) => (
                  <Button
                    key={key}
                    size='sm'
                    variant={statusFilter === key ? (variant || 'primary') : 'outline-secondary'}
                    onClick={() => setStatusFilter(key)}
                  >
                    {label}
                  </Button>
                ))}
              </div>
            )}

            <div className='table-responsive'>
              <Table bordered size='sm' className='align-middle'>
                <thead className='bg-light'>
                  <tr>
                    <th>Fatura No</th>
                    <th>Gönderen</th>
                    <th className='text-end'>Tutar</th>
                    <th>Sonuç</th>
                    {hasErrorColumn && <th>Hata Mesajı</th>}
                    {logs.some((l) => l.expenseInvoiceId) && <th>Fatura</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredLogs.length === 0 && (
                    <tr>
                      <td colSpan={6} className='text-center text-muted py-4'>
                        Gösterilecek kayıt yok.
                      </td>
                    </tr>
                  )}
                  {filteredLogs.map((log) => (
                    <tr key={log.uuid}>
                      <td>{log.invoiceId || '-'}</td>
                      <td>{log.senderName || '-'}</td>
                      <td className='text-end'>{formatAmount(log.payableAmount, log.currency)}</td>
                      <td>
                        {log.isSuccess ? (
                          <Badge bg='success'>Başarılı</Badge>
                        ) : (
                          <Badge bg='danger'>Hatalı</Badge>
                        )}
                      </td>
                      {hasErrorColumn && (
                        <td className='text-danger small'>{log.errorMessage || '-'}</td>
                      )}
                      {logs.some((l) => l.expenseInvoiceId) && (
                        <td>
                          {log.expenseInvoiceId ? (
                            <Link
                              to={`/expenses/expenses/detail/detail/${log.expenseInvoiceId}`}
                              onClick={onClose}
                              className='btn btn-sm btn-light-primary'
                            >
                              Görüntüle
                            </Link>
                          ) : (
                            '-'
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                {filteredLogs.length > 0 && (
                  <tfoot>
                    {Object.entries(totalsByCurrency).map(([currency, total], i) => (
                      <tr key={currency} className='fw-bold bg-light'>
                        <td colSpan={hasErrorColumn ? 2 : 2} className='text-end'>
                          {i === 0 ? 'Toplam' : ''}
                        </td>
                        <td className='text-end'>{formatAmount(total, currency)}</td>
                        <td colSpan={hasErrorColumn ? 2 : 1} />
                      </tr>
                    ))}
                  </tfoot>
                )}
              </Table>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant='primary' onClick={onClose}>
          Tamam
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export {ImportResultModal}
